class CollectionProductGrid extends HTMLElement {
  loading = false
  constructor() {
    super()
    this.$grid = this.querySelector('#main-collection-product-grid')
    this.$loader = this.querySelector('.product-grid__overlay')
    this._handleLoadMore = this._handleLoadMore.bind(this)
    this._createObserver = this._createObserver.bind(this)
    this.$productGrid = this.querySelector('.js-product-grid')
  }

  connectedCallback() {
    this._setLoadMoreListners()
    this._createObserver()
  }

  get nextPageUrl() {
    return this.getAttribute('data-paginate-next')
  }

  set nextPageUrl(value) {
    return this.setAttribute('data-paginate-next', value)
  }

  get previousPageUrl() {
    return this.getAttribute('data-paginate-previous')
  }

  set previousPageUrl(value) {
    return this.setAttribute('data-paginate-previous', value)
  }

  async _fetchPage(nextUrl) {
    if (nextUrl) {
      const response = await fetch(nextUrl)
      const $page = document.createElement('div')
      $page.innerHTML = await response.text()
      const collectionGrid = $page.querySelector('collection-product-grid')
      return {
        products: $page.querySelectorAll(
          '#CollectionProductGrid collection-product-grid #main-collection-product-grid > .product--column'
        ),
        elementToFocus: $page.querySelector('hh-product-form a'),
        loadMoreButton: $page.querySelector(
          'collection-product-grid .product-grid__load-more'
        ),
        paginateNext: collectionGrid.nextPageUrl,
        paginatePrevious: collectionGrid.previousPageUrl,
      }
    }

    throw new Error('No More Page to Load')
  }

  _addProductsAndCards(contents = []) {
    this.$grid.append(...contents)
  }

  _renderPage(page, reset = false) {
    this.nextPageUrl = page.paginateNext
    this.previousPageUrl = page.paginatePrevious
    if (reset) {
      this.$grid.innerHTML = ''
      for (const item of page.products) {
        this.$grid.append(item)
      }
    } else {
      this._addProductsAndCards(page.products)
    }

    if (page.loadMoreButton) {
      this.$loadMoreContainer.remove()
      this.$productGrid.append(page.loadMoreButton)
      this._setLoadMoreListners()
      
      this._fixResultCount()
    }
  }

  _fixResultCount() {
    const badges = document.querySelectorAll('#main-collection-product-grid .product-card__badge');
    const customCount = Array.from(badges).filter(badge => badge.textContent.trim() === 'Recommended').length;
    
    if (customCount > 0) {
      const resultStatus = document.querySelector('.product-grid__load-more p[role="status"]');
      if (resultStatus) {
        const text = resultStatus.textContent.trim();
        const match = text.match(/(\d+)\s+of\s+(\d+)/);
        if (match) {
          const currentShown = parseInt(match[1]) + customCount;
          const originalTotal = parseInt(match[2]) + customCount;
          resultStatus.textContent = `Viewing ${currentShown} of ${originalTotal}`;
        }
      }
    }
  }

  _createObserver() {
    const collectionProductGrid = document.querySelector(
      'collection-product-grid'
    )
    const trackViewItems = this._trackViewItems

    function handleGridInView(entry, observer) {
      if (entry.isIntersecting && window?.gtmContainerId) {
        const url = new URL(window.location.href)
        const pathname = url.pathname
        const segments = pathname.split('/')
        const collectionHandle = segments[2]

        fetch(`/collections/${collectionHandle}/products.json`)
          .then((response) => response.json())
          .then((data) => {
            trackViewItems(
              'view_item_list',
              data?.products,
              collectionProductGrid.dataset?.collectionTitle
            )
          })
          .catch((error) => {
            console.error('Error fetching products:', error)
          })

        observer.disconnect()
      }
    }

    let observer = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          handleGridInView(entry, observer)
        })
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.1,
      }
    )

    if (collectionProductGrid) {
      observer.observe(collectionProductGrid)

      const productColumns = document.querySelectorAll(
        'collection-product-grid .product--column'
      )
      productColumns.forEach((product) => {
        product.addEventListener('click', (event) => {
          const title = product
            .querySelector('.product-card--link')
            .innerText.trim()
          const id = parseInt(
            product.querySelector('input[name="product-id"]').value || 0
          )
          const price = (
            product.querySelector('.price-item--sale').innerText || ''
          )
            .replace(/[$€£,]/g, '')
            .trim()

          trackViewItems(
            'select_item',
            [
              {
                title,
                id,
                price,
              },
            ],
            collectionProductGrid.dataset?.collectionTitle
          )
        })
      })
    }
  }

  _trackViewItems(eventName, products, collectionTitle) {
    const productsPerPage =
      (document.querySelectorAll('.product--column') || []).length || 1

    window.dataLayer = window.dataLayer || []

    const formatData = {
      event: eventName,
      ecommerce: {
        items: (products || []).slice(0, productsPerPage).map((item) => ({
          item_name: item?.title,
          item_id: item?.id,
          price:
            (item?.variants || []).find((variant) => variant.available)
              ?.price || item?.price,
          quantity: 1,
        })),
        item_list_name: collectionTitle,
      },
    }
    window.dataLayer.push(formatData)
  }

  _setLoadMoreListners() {
    if (this.$loadMore) {
      this.$loadMore.removeEventListener('click', this._handleLoadMore)
    }

    this.$loadMore = this.querySelector('[data-load-more]')
    this.$loadMoreContainer = this.querySelector('.product-grid__load-more')

    if (this.$loadMore) {
      this.$loadMore.addEventListener('click', this._handleLoadMore)
    }
  }

  async _handleLoadMore(event) {
    event.preventDefault()
    try {
      if (this.loading) {
        return
      }
      this._setLoading(true)
      this.$loadMore.setAttribute('disabled', true)
      this.$loadMoreContainer.classList.add('loading')
      const nextPage = await this._fetchPage(this.nextPageUrl)
      this._renderPage(nextPage)
      nextPage.elementToFocus.focus()
    } catch (error) {
      console.error(error)
    } finally {
      this._setLoading(false)
    }
  }

  _setLoading(loading) {
    this.loading = loading
    this._applyState()
  }

  _applyState() {
    this.classList[this.loading ? 'add' : 'remove']('is-loading')
  }
}

customElements.define('collection-product-grid', CollectionProductGrid)
